export type FriendAccessLevel = 'full-access' | 'restricted-access';

export const ACCESS_LEVELS = {
  FULL: 'full-access' as const,
  RESTRICTED: 'restricted-access' as const,
} as const;

export const ACCESS_LEVEL_LABELS = {
  [ACCESS_LEVELS.FULL]: 'Full access',
  [ACCESS_LEVELS.RESTRICTED]: 'Restricted access',
} as const;

export const ACCESS_LEVEL_DESCRIPTIONS = {
  [ACCESS_LEVELS.FULL]: 'This friend can see your entire cruise calendar automatically. They will see all your upcoming and past cruises without you needing to share each one individually.',
  [ACCESS_LEVELS.RESTRICTED]: 'This friend can only see cruises you explicitly share with them. You control which cruises they can view on a per-cruise basis.',
} as const;

export interface FriendRequest {
  id: string;
  fromUserId: string;
  fromDisplayName?: string; // Deprecated: use fromUsername for display
  fromEmail: string;
  fromUsername?: string;
  toUserId: string;
  toEmail?: string;
  toUsername?: string;
  status: 'pending' | 'accepted' | 'rejected';
  accessLevel: FriendAccessLevel; // Access level the requester wants to give to the recipient
  requesterAccessLevel?: FriendAccessLevel; // What requester gives to recipient (stored after acceptance)
  recipientAccessLevel?: FriendAccessLevel; // What recipient gives to requester (stored after acceptance)
  createdAt: number;
  respondedAt?: number;
}

export interface Friendship {
  friendId: string;
  accessLevel: FriendAccessLevel;
  createdAt: number;
  /** Prefer friendUsername for display; fallback for legacy data */
  friendDisplayName?: string;
  friendEmail: string;
  friendUsername?: string;
}

export interface SharedCruise {
  cruiseId: string;
  sharedWith: string[]; // Array of friendIds
  sharedAt: number;
}

export interface Notification {
  id: string;
  type: 'friend-request' | 'itinerary-update' | 'itinerary-available' | 'cruise-invite';
  fromUserId: string;
  /** Optional explicit title (defaults to dynamic action title if not provided) */
  title?: string;
  /** Display name for notification (username or email in new flow) */
  fromDisplayName: string;
  message: string;
  read: boolean;
  createdAt: number;
  // friend-request fields
  relatedRequestId?: string;
  // itinerary-update and itinerary-available shared fields
  cruiseId?: string;
  cruiseLine?: string;
  shipName?: string;
  sailingDate?: string;
  // itinerary-update only
  changeDescription?: string;
  // cruise-invite fields
  inviteStatus?: 'pending' | 'accepted' | 'rejected';
  invitedCruiseData?: PublicCruiseView;
}

// Public view of a cruise - sanitized for friends
export interface PublicCruiseView {
  id: string;
  cruiseLine: string;
  shipName: string;
  departureDate: string;
  returnDate: string;
  departurePort: string;
  ports: Array<{
    id: string;
    name: string;
    date: string;
    arrivalTime?: string;
    departureTime?: string;
    // excursion is excluded
  }>;
  travelDetails?: Array<{
    id: string;
    type: 'rental-car' | 'train' | 'airplane';
    departureLocation?: string;
    arrivalLocation?: string;
    departureTime?: string;
    arrivalTime?: string;
    // reservationNumber, flightNumber, trainNumber, carType, pickupLocation, returnLocation, pickupTime, returnTime are excluded
  }>;
  diningTime?: string;
  hasDrinkPackage?: boolean;
  hasInternetPackage?: boolean;
  imageUrl?: string;
  created: number;
  userId: string;
  completed?: boolean;
  ratings?: {
    shipExperience?: number;
    service?: number;
    stateroom?: number;
    foodDining?: number;
    barBeverages?: number;
    entertainment?: number;
  };
  // Excluded fields:
  // cabinNumber, cabinType, isGuaranteedCabin
  // bookingNumber
  // cruiseCost, portTaxesFees, additionalCosts
  // gamblingWins, gamblingLosses, onboardExpenses
  // travelAgentName, travelAgentPhone, travelAgentEmail
  // notes
  // loyaltyPointsOverride
}

